import Column from '@components/Column';
import Image from '@components/Image';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { PaymentMethod } from '@models/order';
import useCalculatedOrder from '@store/item/hooks/useCalculatedOrder';
import { orderActions } from '@store/order/orderSlice';
import { RootState } from '@store/store';
import { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface Props {
    children?: ReactNode;
    icon?: any;
    selectIcon?: any;
    method: PaymentMethod;
}

function MethodItem({
  children, icon, selectIcon, method,
}:Props) {
  const { paymentMethod } = useSelector((state: RootState) => state.order);
  const dispatch = useDispatch();
  const isSelected = method === paymentMethod;
  const updatePaymentMethod = (pm: PaymentMethod) => {
    dispatch(orderActions.setPaymentMethod(pm));
  };
  const iconNode = (() => {
    switch (method) {
      case 'CREDIT_CARD':
        return <CreditCardIcon selected={isSelected}/>;
      case 'PREPAID_CARD':
        return <PrepaidCardIcon selected={isSelected}/>;
      default:
        return <Image src={isSelected ? selectIcon : icon}
                              alt={method}/>;
    }
  })();
  return (
        <Column align='center'
                spacing={7}
                onClick={() => {
                  updatePaymentMethod(method);
                }}>
            {iconNode}
            <Text color={isSelected ? colors.gray9 : colors.gray6}
                  fontWeight={isSelected ? 'bold' : 'regular'} >
                {children}
            </Text>
        </Column>
  );
}

interface IconProps {
    selected: boolean
}

function CreditCardIcon({ selected }: IconProps) {
  return (
        <DynamicWrapper selected={selected}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4.48438" y="6.75" width="21.0129" height="16.125" fill={selected ? '#FFFFFF' : colors.gray2}/>
                <path
                    d="M15.6918 22.6269H12.4052H5.417C4.63441 22.6269 4 21.9925 4 21.2099V13.3654V8.167C4 7.38441 4.63441 6.75 5.417 6.75H14.9914H24.5657C25.3483 6.75 25.9827 7.38441 25.9827 8.167V13.3654V16.7833"
                    stroke="#202326" strokeWidth="2"/>
                <path d="M8.52539 12.0424H12.4047" stroke="#202326" strokeWidth="2.1255" strokeLinecap="round"/>
                <ellipse cx="21.8215" cy="21.2625" rx="5.77854" ry="5.9125" fill={selected ? '#FFFFFF' : colors.gray2}/>
                <path
                    d="M21.9961 14.9167C25.2907 14.9167 28.001 17.6562 28.001 21.0836C28.0008 24.511 25.2906 27.2497 21.9961 27.2497C18.7018 27.2494 15.9924 24.5108 15.9922 21.0836C15.9922 17.6563 18.7017 14.9169 21.9961 14.9167Z"
                    stroke="#202326" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path
                    d="M21.9971 18.3874C22.2935 18.3876 22.5558 18.5815 22.6426 18.8649L23.4482 21.5016L24.2539 18.8649L24.3066 18.739C24.4568 18.4636 24.785 18.3215 25.0967 18.4167C25.4082 18.5121 25.6009 18.8134 25.5713 19.1257L25.5449 19.2595L25.0605 20.8444H25.4658C25.7938 20.8444 26.0595 21.1103 26.0596 21.4382C26.0596 21.7662 25.7938 22.0319 25.4658 22.0319H24.6982L24.0938 24.0104C24.0071 24.2941 23.7448 24.4878 23.4482 24.488C23.1515 24.488 22.8894 24.2942 22.8027 24.0104L21.9971 21.3727L21.1914 24.0104C21.1048 24.2941 20.8425 24.4878 20.5459 24.488C20.2492 24.488 19.9871 24.2942 19.9004 24.0104L19.2959 22.0319H18.5273C18.1994 22.0318 17.9336 21.7661 17.9336 21.4382C17.9337 21.1103 18.1995 20.8445 18.5273 20.8444H18.9336L18.4492 19.2595L18.4229 19.1257C18.3932 18.8132 18.5856 18.512 18.8975 18.4167C19.2092 18.3217 19.5375 18.4635 19.6875 18.739L19.7402 18.8649L20.5459 21.5016L21.3516 18.8649L21.3916 18.7634C21.5039 18.5362 21.7374 18.3874 21.9971 18.3874Z"
                    fill="#202326"/>
            </svg>
        </DynamicWrapper>
  );
}

function PrepaidCardIcon({ selected }: IconProps) {
  return (
        <DynamicWrapper selected={selected}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M21.1043 26.7781L23.3219 21.4146L25.0001 17.3557L27.2178 11.9921C27.4991 11.3116 27.1756 10.5319 26.4951 10.2505L20.5396 7.78815L18.9955 7.14972C18.4024 6.90451 17.7188 7.11659 17.3686 7.65443L8.4863 21.2978C8.02979 21.999 8.32444 22.9425 9.09886 23.2594L9.60654 23.467L13.4072 25.0385L19.3627 27.5008C20.0432 27.7822 20.823 27.4586 21.1043 26.7781Z"
                    fill={selected ? '#FFFFFF' : colors.gray2} stroke="black" strokeWidth="2"/>
                <path
                    d="M17.606 21.3156L17.606 15.5117L17.606 11.1195L17.606 5.31559C17.606 4.57921 17.0091 3.98226 16.2727 3.98226L9.82826 3.98226L5.60604 3.98226C4.86966 3.98226 4.2727 4.57921 4.2727 5.31559L4.2727 8.64893L4.2727 13.3156L4.27271 21.3156C4.27271 22.052 4.86966 22.6489 5.60604 22.6489L9.82826 22.6489L16.2727 22.6489C17.0091 22.6489 17.606 22.052 17.606 21.3156Z"
                    fill={selected ? '#FFFFFF' : colors.gray2} stroke="black" strokeWidth="2"/>
                <circle cx="9.60604" cy="9.31551" r="1.33333" fill="black"/>
            </svg>

        </DynamicWrapper>
  );
}

const DynamicWrapper = styled.div<{
    selected: boolean;
}>`
    width: 52px;
    height: 52px;
    border-radius: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ selected }) => (selected ? colors.primary : colors.gray2)};
`;

export default MethodItem;
